import React, { useState, useRef, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import Select from "components/select";
import { SkillQuestionSelect } from "components/skillQuestion";
import { CompanyValuesSelect } from "components/companyValuesQuestion";
import LikertScaleQuestion, { getLikertPoints } from "components/likertScaleQuestion";
import MultiChoiceQuestion, {
  MULTICHOICE_OTHER_KEY,
  MULTICHOICE_OTHER_TEXT_MAX,
} from "components/multichoiceQuestion";
import Button from "components/button";
import Card from "components/card";
import Stepper from "components/stepper";

import { t } from "intl/index";
import debounce from "lodash/debounce";
import axios from "axios";

import styles from "components/wizard/styles";
import useAppStore from "stores/appStore";

const selectTypes = {
  MULTI: "multiSelect",
  SINGLE: "singleSelect",
  SINGLE_WITH_OPTION: "singleSelectWithOption",
  COMPANY_VALUES: "companyValues",
  LIKERT_SCALE: "likertScale",
  MULTICHOICE: "multichoice",
};

const Wizard = ({
  options: propsOptions,
  questions,
  activeStep,
  onNextClick,
  onBackClick,
  settings,
  prevAnswers = [],
  apiError = "",
}) => {
  const [answers, setAnswers] = useState([]);
  const [prevAnswersLoaded, setPrevAnswersLoaded] = useState(false);
  const [error, setError] = useState("");
  const [skillError, setSkillError] = useState("");
  const [search, setSearch] = useState("");
  const [options, setOptions] = useState(propsOptions ?? []);
  const [version, setVersion] = useState(null);
  const [multichoiceOptionKey, setMultichoiceOptionKey] = useState("");
  const [multichoicePeers, setMultichoicePeers] = useState([]);
  const [multichoiceOtherText, setMultichoiceOtherText] = useState("");

  const isLastStep = questions.length - 1 === activeStep;
  const isFirstStep = activeStep === 0;
  const { lang, accessToken } = useAppStore();
  const singleWithOptionRef = useRef();
  const companyValuesRef = useRef();
  const likertRef = useRef();

  const url = new URL(window.location);
  const surveyId = url.searchParams.get("surveyId");
  const hash = url.searchParams.get("hash");

  useEffect(() => {
    const fetchVersion = async () => {
      try {
        const { data } = await axios.get(
          `${process.env.REACT_APP_FASTIFY_API_URL}/v1/version`
        );
        setVersion(data.version);
      } catch (fetchError) {
        console.error(`Error fetching version: ${fetchError.message}`);
      }
    };

    fetchVersion();
  }, []);

  const fetchOptions = useCallback(
    debounce(async (searchTerm) => {
      if (!version) return;

      try {
        const response = await axios.get(
          `${process.env.REACT_APP_FASTIFY_API_URL}/${version}/survey-participants/name-email-search`,
          {
            params: { search: searchTerm, surveyId },
            headers: { Authorization: `Bearer ${accessToken}` },
          }
        );

        setOptions(
          response.data
            .filter((participant) => String(participant.id) !== String(hash))
            .sort((a, b) => a.name.localeCompare(b.name))
            .map((participant) => ({
              label: `${participant.name} (${participant.email.split("@")[0]})`,
              value: participant.id,
            }))
        );
      } catch (fetchError) {
        console.error("Error fetching survey participants:", fetchError);
      }
    }, 300),
    [version, surveyId, accessToken, hash]
  );

  useEffect(() => {
    if (version) {
      fetchOptions(search ?? "");
    }
  }, [search, version, fetchOptions]);

  useEffect(() => {
    if (version) {
      fetchOptions("");
    }
  }, [version, fetchOptions]);

  const handleInputChange = (inputValue) => {
    setSearch(inputValue);
  };

  useEffect(() => {
    if (prevAnswers.length > 0 && answers.length === 0 && !prevAnswersLoaded) {
      setAnswers(prevAnswers);
      setPrevAnswersLoaded(true);
    }
  }, [answers, prevAnswers, prevAnswersLoaded]);

  useEffect(() => {
    const currentQuestion = questions[activeStep];
    if (!currentQuestion || currentQuestion.type !== selectTypes.MULTICHOICE) {
      setMultichoiceOptionKey("");
      setMultichoicePeers([]);
      setMultichoiceOtherText("");
      return;
    }

    const selected = Array.isArray(prevAnswers) && prevAnswers[0]?.option_key
      ? String(prevAnswers[0].option_key)
      : "";
    setMultichoiceOptionKey(selected);
    setMultichoiceOtherText(
      selected === MULTICHOICE_OTHER_KEY ? String(prevAnswers[0]?.other_text || "") : ""
    );
    const peers = Array.isArray(prevAnswers) && Array.isArray(prevAnswers[0]?.peers)
      ? prevAnswers[0].peers
      : [];
    setMultichoicePeers(peers.map((peerId) => options.find((opt) => String(opt.value) === String(peerId))).filter(Boolean));
  }, [activeStep, questions, prevAnswers]);

  const handleSubmit = () => {
    const currentQuestion = questions[activeStep];
    const questionType = currentQuestion.type;
    const isRequired = currentQuestion.is_required !== false;

    if (questionType === selectTypes.SINGLE_WITH_OPTION) {
      const values = singleWithOptionRef?.current?.getState();
      const allSkills = values.map((value) => value.skill);

      if (isRequired && allSkills.includes("")) {
        setSkillError(t(lang).EMPTY_ERROR);
        return;
      }

      onNextClick(values.length > 0 ? values : null);
      setAnswers([]);
      setError("");
      setSkillError("");
      return;
    }

    if (questionType === selectTypes.COMPANY_VALUES) {
      const values = companyValuesRef?.current?.getState();

      if (isRequired && (!values || values.length === 0)) {
        setError(t(lang).EMPTY_ERROR);
        return;
      }

      onNextClick(values.length > 0 ? values : null);
      setAnswers([]);
      setError("");
      return;
    }

    if (questionType === selectTypes.LIKERT_SCALE) {
      const ratings = likertRef?.current?.getState?.() || [];
      const peerEnabled = currentQuestion.peer_nomination_enabled === true;
      const selectedRatings = peerEnabled
        ? ratings.filter((row) => row.user || row.weight !== null)
        : ratings.filter((row) => row.weight !== null);

      if (isRequired && selectedRatings.length === 0) {
        setError(t(lang).EMPTY_ERROR);
        return;
      }

      const hasIncompleteRating = peerEnabled
        ? selectedRatings.some((row) => !row.user || row.weight === null)
        : selectedRatings.some((row) => row.weight === null);
      if (hasIncompleteRating) {
        setError(t(lang).LIKERT_INCOMPLETE_ERROR);
        return;
      }

      const allowedPoints = getLikertPoints(currentQuestion.likert);
      const normalizedRatings = selectedRatings.map((row) => ({
        value: peerEnabled ? row.user.value : hash,
        weight: Number(row.weight),
      }));

      const hasInvalidWeight = normalizedRatings.some((row) => !allowedPoints.includes(row.weight));
      if (hasInvalidWeight) {
        setError(t(lang).LIKERT_INVALID_ERROR);
        return;
      }

      onNextClick(normalizedRatings.length > 0 ? normalizedRatings : null);
      setAnswers([]);
      setError("");
      return;
    }

    if (questionType === selectTypes.MULTICHOICE) {
      const optionKey = multichoiceOptionKey.trim();
      const peerEnabled = currentQuestion.peer_nomination_enabled === true;
      const isOther = currentQuestion.multichoice?.allow_other === true
        && optionKey === MULTICHOICE_OTHER_KEY;
      const otherText = multichoiceOtherText.trim();
      if (isRequired && !optionKey) {
        setError(t(lang).EMPTY_ERROR);
        return;
      }
      // "Other" with blank text is never a valid answer, even on optional questions.
      if (isOther && !otherText) {
        setError(t(lang).MULTICHOICE_OTHER_TEXT_REQUIRED);
        return;
      }
      if (isOther && otherText.length > MULTICHOICE_OTHER_TEXT_MAX) {
        setError(t(lang).MULTICHOICE_OTHER_TEXT_TOO_LONG);
        return;
      }
      if (peerEnabled && isRequired && multichoicePeers.length === 0) {
        setError(t(lang).MULTICHOICE_PEER_REQUIRED_ERROR || t(lang).EMPTY_ERROR);
        return;
      }
      onNextClick(optionKey ? [{
        option_key: optionKey,
        peers: multichoicePeers.map((peer) => peer.value),
        ...(isOther ? { other_text: otherText } : {}),
      }] : null);
      setError("");
      return;
    }

    if (isRequired && answers.length === 0) {
      setError(t(lang).EMPTY_ERROR);
      return;
    }

    setPrevAnswersLoaded(false);
    onNextClick(answers.length > 0 ? answers : null);
    setAnswers([]);
    setError("");
  };

  const handleBack = () => {
    onBackClick();
    setAnswers([]);
    setPrevAnswersLoaded(false);
    setError("");
  };

  const handleChange = (choices) => {
    if (choices && choices.length === 20) {
      setError(t(lang).MAX_SELECT_ERROR + " 20");
      return;
    }
    setAnswers(choices || []);
    setError("");
  };

  const removeMultichoicePeer = (peerValue) => {
    setMultichoicePeers((prev) =>
      prev.filter((peer) => String(peer.value) !== String(peerValue))
    );
  };

  return (
    <Card>
      <styles.QuestionWrapper>
        <h3>{questions[activeStep].title}</h3>
        {activeStep + 1}. &nbsp; {questions[activeStep].name}
      </styles.QuestionWrapper>
      {
        {
          [selectTypes.MULTI]: (
            <Select
              options={options}
              onChange={handleChange}
              onInputChange={handleInputChange}
              value={answers}
              isMulti
              placeholder={t(lang).SELECT_INPUT}
            />
          ),
          [selectTypes.LIKERT_SCALE]: (
            <LikertScaleQuestion
              options={options}
              componentRef={likertRef}
              likert={questions[activeStep].likert}
              langMap={t(lang)}
              peerNominationEnabled={questions[activeStep].peer_nomination_enabled === true}
            />
          ),
          [selectTypes.MULTICHOICE]: (
            <>
              {questions[activeStep].peer_nomination_enabled === true && (
                <>
                  <Select
                    options={options}
                    onChange={(choices) => {
                      setMultichoicePeers(choices || []);
                      setError("");
                    }}
                    onInputChange={handleInputChange}
                    value={multichoicePeers}
                    isMulti
                    placeholder={t(lang).MULTICHOICE_PEER_PLACEHOLDER || t(lang).SELECT_INPUT}
                  />
                  {multichoicePeers.length > 0 && (
                    <div style={{ marginTop: "8px", marginBottom: "8px" }}>
                      {multichoicePeers.map((peer) => (
                        <Button
                          key={`peer-remove-${peer.value}`}
                          onClick={() => removeMultichoicePeer(peer.value)}
                          title={`- ${peer.label}`}
                        />
                      ))}
                    </div>
                  )}
                </>
              )}
              <MultiChoiceQuestion
                options={questions[activeStep].multichoice?.options || []}
                selectedOptionKey={multichoiceOptionKey}
                onChange={(value) => {
                  setMultichoiceOptionKey(value);
                  if (value !== MULTICHOICE_OTHER_KEY) {
                    setMultichoiceOtherText("");
                  }
                  setError("");
                }}
                allowOther={questions[activeStep].multichoice?.allow_other === true}
                otherText={multichoiceOtherText}
                onOtherTextChange={(value) => {
                  setMultichoiceOtherText(value);
                  setError("");
                }}
                otherLabel={t(lang).MULTICHOICE_OTHER_LABEL}
                otherPlaceholder={t(lang).MULTICHOICE_OTHER_PLACEHOLDER}
                canClear={questions[activeStep].is_required === false}
                clearLabel={t(lang).MULTICHOICE_CLEAR_SELECTION}
              />
            </>
          ),
          [selectTypes.SINGLE]: (
            <Select
              options={options}
              onChange={handleChange}
              value={answers}
              placeholder={t(lang).SELECT_INPUT}
              isMulti={false}
            />
          ),
          [selectTypes.SINGLE_WITH_OPTION]: (
            <SkillQuestionSelect
              options={options}
              placeholder={t(lang).SELECT_INPUT}
              componentRef={singleWithOptionRef}
              langMap={t(lang)}
              error={skillError}
              onSkillChange={() => setSkillError("")}
            />
          ),
          [selectTypes.COMPANY_VALUES]: (
            <CompanyValuesSelect
              options={options}
              placeholder={t(lang).SELECT_INPUT}
              componentRef={companyValuesRef}
              langMap={t(lang)}
            />
          ),
        }[questions[activeStep].type]
      }

      <styles.Error>{error || apiError}</styles.Error>
      <Stepper activeStep={activeStep} steps={questions.length} />
      <styles.Back>
        <Button isDisabled={isFirstStep} onClick={handleBack} title={t(lang).BACK_BUTTON} />
      </styles.Back>
      <styles.Next>
        <Button type="primary" onClick={handleSubmit} title={isLastStep ? t(lang).FINISH_BUTTON : t(lang).NEXT_BUTTON} />
      </styles.Next>
    </Card>
  );
};

Wizard.propTypes = {
  options: PropTypes.array.isRequired,
  questions: PropTypes.array.isRequired,
  activeStep: PropTypes.number.isRequired,
  onNextClick: PropTypes.func.isRequired,
  onBackClick: PropTypes.func.isRequired,
  settings: PropTypes.object.isRequired,
  apiError: PropTypes.string,
  prevAnswers: PropTypes.array,
};

export default Wizard;
